import type { DetailedPinWithChecked } from '~/modules/design-board/component/all-pins/AllPins';
import { VotingControl } from './voting-control';
import { CommentButtonWithHoverPreview } from '@modules/build-hub-comment/components/CommentButtonWithHoverPreview';
import { useCallback, memo } from 'react';

import { useMoodboardItemDetailDrawer } from './item-detail-drawer';
import { useBuildHubBoardItemVoting } from '~/modules/build-hub/hooks/use-build-hub-board-item-voting';
import { PinOptionsMenu } from './PinOptionsMenu';

import { ReactionTypeEnum } from '../../../../../../generated/graphql';

interface MoodboardItemActionBarProps {
    canVote: boolean;
    canComment: boolean;
    canEdit?: boolean;
    pin: DetailedPinWithChecked;
    boardLink: string;
    boardId?: number;
    onDelete?: () => void;
}

const MoodboardItemActionBarComponent = ({
    canVote,
    canComment,
    canEdit = false,
    pin,
    boardLink,
    boardId,
    onDelete,
}: MoodboardItemActionBarProps) => {
    const { openDialog } = useMoodboardItemDetailDrawer();

    const {
        upVoteCollaborators,
        downVoteCollaborators,
        isVotedUp,
        isVotedDown,
        handleUpVote,
        handleDownVote,
        handleRevokeVote,
    } = useBuildHubBoardItemVoting({
        targetId: pin.ID,
    });

    const handleVoteUp = useCallback(() => {
        if (isVotedUp) {
            handleRevokeVote(ReactionTypeEnum.UpVote);
        } else {
            handleUpVote();
        }
    }, [isVotedUp, handleRevokeVote, handleUpVote]);

    const handleVoteDown = useCallback(() => {
        if (isVotedDown) {
            handleRevokeVote(ReactionTypeEnum.DownVote);
        } else {
            handleDownVote();
        }
    }, [isVotedDown, handleRevokeVote, handleDownVote]);

    const handleCommentClick = useCallback(
        (e: React.SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
            openDialog(pin);
        },
        [openDialog, pin]
    );

    if (!canVote && !canComment && !canEdit) {
        return null;
    }

    return (
        <div className="flex flex-row justify-between">
            <div className="flex flex-row items-center gap-2">
                {canComment && (
                    <CommentButtonWithHoverPreview
                        itemId={pin.ID}
                        commentsCount={pin.commentsCount}
                        unreadCount={pin.unreadCount ?? 0}
                        inline
                        onClick={handleCommentClick}
                        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2"
                    />
                )}
                {canVote && (
                    <VotingControl
                        hasVotedUp={isVotedUp}
                        hasVotedDown={isVotedDown}
                        upVoteCollaborators={upVoteCollaborators}
                        downVoteCollaborators={downVoteCollaborators}
                        onVoteUp={handleVoteUp}
                        onVoteDown={handleVoteDown}
                    />
                )}
            </div>
            {canEdit && (
                <div className="flex justify-end">
                    <PinOptionsMenu
                        pin={pin}
                        canEdit={canEdit}
                        boardLink={boardLink}
                        boardId={boardId}
                        onDelete={onDelete}
                    />
                </div>
            )}
        </div>
    );
};

export const MoodboardItemActionBar = memo(MoodboardItemActionBarComponent);
